import axios from "axios";
import expirePlugin from "store/plugins/expire"
import store from "store";
import jsbridge from "./JSbridge.js";
import {
    EventBus
} from "../Bus";
import badgeService from "@/Services/badgeService";
import {removeContact, addContact, updateContacts, contactUnread} from "@/store/contactsStore";

store.addPlugin(expirePlugin);

const TOKEN_KEY = "mm-chat.token";
const REFRESH_TOKEN_KEY = "mm-chat.refresh.token";
const CACHE_KEY_PROFILE = 'profile';
const CACHE_KEY_DISABLE_NOTIFICATION = 'disable_notification';

const ERROR_CODE = {
    AUTH: 406
};

class ValidateError extends Error {
    constructor(validate) {
        super();
        this.validate = validate;
    }

    getValidate() {
        return Object.fromEntries(Object.entries(this.validate || {}).map(([key, val]) => {
            return [key, val[0]];
        })) ;
    }
}

class UserService {
    static userMapping = {};
    static meCache = null;

    static get TokenKey() {
        return TOKEN_KEY;
    }

    constructor() {
        this.token = store.get(TOKEN_KEY) || "";
        this.api = global.API_URI || "http://mm-chat.demo2.mixmedia.com/api";


        this.http = axios.create({
            baseURL: this.api,
        });

        // Add a request interceptor
        this.http.interceptors.request.use((config) => {
            // console.log(this.token);
            config = Object.assign(config, {
                headers: {
                    Authorization: store.get(TOKEN_KEY) || this.token
                }
            });

            // console.log(`request ${config.method} ${config.url} \n body:\n`);
            // console.log(config.params);

            return config;
        });

        this.http.interceptors.response.use(function (resp) {
            // console.log(`response:\n`);
            // console.log(resp.data);
            let json = resp.data;
            if (json && json.status == 1) {
                return json.data;
            }
            if (json && json.error && json.error.validate) {
                return Promise.reject(new ValidateError(json.error.validate));
            }
            if (json && json.error) {
                if (json.error.code == ERROR_CODE.AUTH) {
                    EventBus.dispatchAuthError();
                }
                if (json.error.msg) {
                    return Promise.reject(new Error(json.error.msg));
                }
                return Promise.reject(json.error);
            }

            return Promise.reject();
        });
    }

    login(user_name, pwd) {

        return jsbridge.getFCMToken()
            .catch((error) => {
                console.error(error);
                return Promise.resolve(false);
            })
            .then((token) => {
                // console.log(`auth login fcm token ${token}`);
                let params = {
                    login_name: user_name,
                    pwd: pwd
                };
                if (token && token.length > 0) {
                    params = {
                        ...params,
                        token,
                    }
                }
                return this.http.post("/auth", params)
            })
            .then((data) => {
                this.token = data.token;
                store.set(TOKEN_KEY, this.token);
                if (data.fresh_token) {
                    store.set(REFRESH_TOKEN_KEY, data.fresh_token);
                }
                return Promise.resolve(this.token);
            })
    }

    refresh() {
        const fresh_token = store.get(REFRESH_TOKEN_KEY);
        return this.http.post("/auth/refresh", {
                fresh_token
            })
            .then((data) => {
                this.token = data.token;
                store.set(TOKEN_KEY, this.token);
                if (data.fresh_token) {
                    store.set(REFRESH_TOKEN_KEY, data.fresh_token);
                }
                return Promise.resolve(this.token);
            })
    }

    sendVCode(params = {mobile:"", email: ""}) {
        return this.http.post("/auth/vcode", params)
    }

    resetPwd(params = {email: "", mobile: "", pwd: "", vcode: ""}) {
        return this.http.post("/auth/reset", params)
    }

    logout() {
        return this.http.get("/auth/logout")
            .then(() => {
                store.remove(TOKEN_KEY);
                store.remove(REFRESH_TOKEN_KEY);
                store.remove(CACHE_KEY_PROFILE);
                this.token = "";
                return Promise.resolve();
            })
    }

    me(nocache = false) {
        //除了第一次获取之后，所有之后所有请求都从缓存获取
        const info = store.get(CACHE_KEY_PROFILE);
        const now = Math.floor((new Date()).getTime() / 1000);
        const isDisableNotification = store.get(CACHE_KEY_DISABLE_NOTIFICATION);
        if (!nocache && info) {
            if (!isDisableNotification && (!info.subscribe_expire || (info.subscribe_expire < now))) {
                console.log(`dispatchOneSignalTokenExpired`, info.subscribe_expire, now);
                EventBus.dispatchOneSignalTokenExpired();
            }
            return Promise.resolve(info);
        }

        return this.http.get("/user/me")
            .then((data) => {
                /**
                 * 首次获取 profile 之后cache 数据，
                 * 直至 onesignal token 过期前 1 小时,
                 * 如果 onesignal token 不存在 (0) 或者 过期了,
                 * 触发 `onesignal.expired` 事件
                 */
                store.removeExpiredKeys();
                const expire_at = (data.token_expire || now + 86400) - 3600;
                store.set(CACHE_KEY_PROFILE, data, expire_at * 1000);
                if (!isDisableNotification && (!data.subscribe_expire || (data.subscribe_expire < now))) {
                    console.log(`dispatchOneSignalTokenExpired`, data.subscribe_expire, now);
                    EventBus.dispatchOneSignalTokenExpired();
                }

                return Promise.resolve(data);
            })
            .catch((error) => {
                this.token = "";
                store.remove(TOKEN_KEY);
                return Promise.reject(error);
            })
    }

    onesignal(uuid, app_id = null) {
        let args = {
            uuid,
        };
        if (app_id) {
           args = {
               ...args,
               app_id,
           };
        }
        store.remove(CACHE_KEY_DISABLE_NOTIFICATION);
        return this.http.post("/user/me/onesignal", args)
    }

    unsubscribeNotifications() {
        store.set(CACHE_KEY_DISABLE_NOTIFICATION, {disabled: true});
        return this.http.post("/user/me/onesignal/unsubscribe")
    }

    users(params = {
        search:null,
        refresh: false,
    }) {
        const opt = {
            search: null,
            refresh: false,
            ...params,
        };

        const handler = () => {
            let params = {};
            if (opt.search) {
                params = {
                    search: opt.search
                }
            }

            return this.http.get("/contacts", {params})
                .then((resp) => {
                    if (!opt.search) {
                        const list = Array.from(resp).filter((row) => {
                            return row.unread > 0;
                        }).map((row) => {
                            return row.uid
                        });
                        badgeService.update(list)
                        updateContacts(Array.from(resp));
                    }
                    return Promise.resolve(resp);
                })
                .catch((error) => {
                    this.token = "";
                    store.remove(TOKEN_KEY);
                    return Promise.reject(error);
                })
        }

        if (opt.search || opt.refresh) {
            return handler();
        }

        if (response) {
            // console.log('from cache');
            setTimeout(handler, 1);
            return Promise.resolve(response);
        }

        return handler();
    }

    addUser(channel_id, name) {
        return this.http.post("/user/whatsapp/add", {
            channel_id: channel_id,
            name: name
        })
    }

    forgetUserCache(uid) {
        delete UserService.userMapping[uid];
    }

    getUser(uid) {
        return new Promise((resolve, reject) => {
            if (UserService.userMapping[uid]) {
                resolve(UserService.userMapping[uid]);
                return;
            }

            this.http.get(`/user/${uid}`).then((user) => {
                UserService.userMapping[uid] = user;
                resolve(UserService.userMapping[uid]);
            }).catch(reject);
        });
    }

    user(uid, name) {
        if (UserService.userMapping[uid]) {
            delete UserService.userMapping[uid];
        }
        return this.http.post("/user", {
            uid: uid,
            name: name
        }).then((resp) => {
            this.users({refresh: true});
            return Promise.resolve(resp);
        })
    }

    editMe(name, pwd = null) {
        return this.http.post("/user/me/edit", {
            name,
            pwd
        }).then((resp) => {
            this.me(true);
            return Promise.resolve(resp);
        });
    }

    remove(uid) {
        return this.http.post("/contacts/remove/" + uid, {
            uid: uid
        }).then(resp => {
            this.users({refresh: true});
            return Promise.resolve(resp);
        })
    }

    mark_as_unread(uid) {
        return this.http.post("/contacts/unread/" + uid, {uid: uid})
            .then((data) => {
                contactUnread(uid);
                return Promise.resolve(data);
            });
    }

    read_all() {
        return this.http.post("/contacts/allread")
            .then((resp) => {
                this.users({refresh: true});
                return Promise.resolve(resp);
            })
    }

    search_chat_record(query) {
        return this.http.get("/msg/search", {
            params: {
                search: query
            }
        })
    }

    total() {
        return this.http.post("/user/channel/total", {
            channel: "whatsapp",
        })
    }

    motorsInfo(uid) {
        return this.http.get(`/user/${uid}/motor/info`)
    }
}

export default UserService;
export {ValidateError}
